import { Link } from 'react-router';
import { aapneValget } from '../lib/informasjonskapsler';
import {
  BESOKSSTATISTIKK_MAANEDER,
  KAMPANJE_MAANEDER,
  KLIKKID_DAGER,
  LAGRINGSTID_MAANEDER,
  VARSEL_DAGER,
} from '../lib/lagringstid';
import { SELSKAP } from '../lib/selskap';
import { SIDETITLER, useSidehode } from '../lib/sidehode';
import { SVARLENKE_DAGER } from '../lib/svarlenke';

/**
 * Personvernerklæring for MVP-en.
 *
 * The text describes what the code actually does today. Anything not yet built
 * is called out as such rather than promised — the page is reviewed alongside
 * the data model, not written once and forgotten.
 */
export function PrivacyPage() {
  useSidehode(SIDETITLER.personvern, '/personvern');
  return (
    <div className="legal-page">
      <Link to="/" className="small">
        ← Tilbake til forsiden
      </Link>
      <h1>Personvern</h1>
      <p className="muted">
        Sist oppdatert ved oppstart av tjenesten. Vår Dag er behandlingsansvarlig for
        opplysningene i tjenesten — både for dere som bruker den, og for gjestene dere legger
        inn.
      </p>

      <h2>Hva vi lagrer</h2>
      <ul>
        <li>
          <strong>Om deg:</strong> navn, e-postadresse, kryptert passord, tidspunkt for godtatte
          vilkår og eventuelt samtykke til e-post fra oss. I tillegg hvilken kanal og hvilken
          kampanje dere kom inn på – se «Annonsemåling» lenger nede.
        </li>
        <li>
          <strong>Om bryllupet:</strong> navnene til brudeparet, dato, sted, anslått antall
          gjester og budsjettramme.
        </li>
        <li>
          <strong>Om gjestene:</strong> navn, og det dere selv velger å legge inn av e-post,
          telefon, husstand, allergier og notater.
        </li>
        <li>
          <strong>Teknisk:</strong> en behandlingsprotokoll over viktige handlinger (innlogging,
          eksport, sletting). IP-adresser lagres bare som en enveis-hash.
        </li>
        <li>
          <strong>Driftsmåling:</strong> at en side ble lastet, hvor lang tid en forespørsel
          tok, og hva som sto i en feilmelding dersom noe gikk galt. Målingene brukes til å
          oppdage og rette feil, ikke til markedsføring, og de settes ingen
          informasjonskapsler for.
        </li>
        <li>
          <strong>Besøksstatistikk:</strong> bare hvis du har sagt ja, en tilfeldig ID i
          nettleseren, når du besøkte forsiden, prislista og registreringen, og hvilken konto
          ID-en hører til når du har logget inn – se «Besøksstatistikk» lenger nede.
        </li>
        <li>
          <strong>Tilbakemeldinger:</strong> vurderinger og fritekst dere selv sender oss om
          tjenesten. Friteksten lagres kryptert, leses bare av oss, og brukes til å forbedre
          Vår Dag – ikke til markedsføring. Den følger med i innsynskopien og slettes med
          kontoen.
        </li>
      </ul>

      <h2>Gjestene, og hva behandlingen hviler på</h2>
      <p>
        Gjestene har ingen konto hos oss og har ikke inngått noe med oss. Likevel er det{' '}
        <strong>vi som er behandlingsansvarlige</strong> for opplysningene om dem: det er vi som har
        bestemt hvilke felt som finnes, hvordan svarlenken virker, hvordan opplysningene krypteres
        og når de slettes. Brudeparet bestemmer hvem som skal stå på lista og hva som noteres, og
        har pliktene som følger med i <Link to="/vilkaar">bruksvilkårene</Link> punkt 4.
      </p>
      <p>
        Grunnlaget er <strong>berettiget interesse</strong> (personvernforordningen art. 6 nr. 1
        bokstav f). Et par som skal holde et selskap, må kunne holde styr på hvem som kommer, og en
        gjest som har fått en invitasjon, venter at svaret hennes blir notert et sted. Vi bruker
        ikke opplysningene til noe annet: ingen markedsføring, ingen analyse på tvers av bryllup,
        intet salg.
      </p>
      <p>
        <strong>Allergier og kosthold</strong> står i en annen stilling. De kan si noe om helse, og
        hviler derfor på et <strong>uttrykkelig samtykke</strong> (art. 9 nr. 2 bokstav a): gjesten
        krysser av selv når hun skriver dem inn, og skriver brudeparet dem inn på hennes vegne,
        bekrefter de at de har spurt og fått ja. Uten avkryssing lagres kostholdet ikke — men svaret
        om at hun kommer, tas imot likevel.
      </p>
      <p>
        Gjestene har de samme rettighetene som alle andre, og bruker dem mot oss.{' '}
        <strong>Innsyn og sletting ligger på svarlenken deres</strong>, under «Hva har dere om
        meg?»: de kan be om en kopi av alt vi har, eller om å bli slettet. Kopien og bekreftelsen
        går til e-postadressen som står på gjesten, aldri til nettleseren som spurte — svarlenken
        er en nøkkel som kan ha blitt videresendt, og en side som viste allergien til den som har
        lenken, ville tatt tilbake det svarsida er bygget for å beskytte.
      </p>
      <p>
        En sletting utføres når gjesten har bekreftet den i e-posten. Brudeparet får vite at
        gjesten er ute av lista, men ikke hvorfor, og adressen kan ikke legges inn i det bryllupet
        igjen — en sletting som kunne skrives over neste gang noen ser på lista, ville ikke vært en
        sletting. Vi oppbevarer da bare den nøklede hashen av adressen, ikke adressen selv, og den
        slettes sammen med resten av gjestelista.
      </p>
      <p>
        Retting gjør gjesten i svarskjemaet, eller ved å si det til brudeparet — det virker med én
        gang. Har gjesten ingen svarlenke, eller ingen e-postadresse registrert, går veien om{' '}
        <a href={`mailto:${SELSKAP.epost}`}>{SELSKAP.epost}</a>.
      </p>

      <h2>Hvordan opplysningene sikres</h2>
      <ul>
        <li>
          E-post, telefon, allergier og notater om gjestene krypteres med AES-256-GCM før de
          lagres. Nøkkelen ligger utenfor databasen, slik at en database­kopi alene ikke gir
          tilgang til innholdet.
        </li>
        <li>
          Allergi- og kostholdsopplysninger kan si noe om helse. De behandles derfor som en
          særlig kategori (personvernforordningen art. 9) og krypteres alltid.
        </li>
        <li>Passord lagres som bcrypt-hash, aldri i klartekst.</li>
        <li>
          Innlogging skjer med en informasjonskapsel som ikke kan leses av skript i nettleseren.
        </li>
      </ul>

      <h2>Kartet over leverandører</h2>
      <p>
        Kartvisningen bruker Google Maps. Når du åpner den, laster nettleseren din
        kartet direkte fra Google, og Google ser da IP-adressen din, hvilket område av
        kartet du ser på, og opplysninger om nettleseren din. Google er behandlingsansvarlig
        for det de samler inn på den måten, og bruker det etter sine egne vilkår – ikke
        våre.
      </p>
      <p>
        Vi sender ingenting om deg til Google. Nålene, kortene og vurderingene kommer fra
        oss. Men vi kan ikke love hva Google gjør med besøket, og derfor sier vi det rett
        ut i stedet.
      </p>
      <p>
        <strong>Vil du slippe det, bruk listevisningen.</strong> Den viser de samme
        leverandørene, med de samme vurderingene, og den laster ingenting fra Google. Du
        velger visning øverst på siden, og valget gjelder til du bytter selv.
      </p>

      <h2>Måling av drift</h2>
      <p>
        For å oppdage feil før dere gjør det, sender både nettsiden og serveren tekniske
        målinger til Application Insights hos Microsoft Azure. Det som sendes er hva slags
        side eller forespørsel det gjaldt, hvor lang tid den tok, og feilmeldingen dersom
        noe feilet.
      </p>
      <p>
        Adressene renskes før de forlater maskinen. Identifikatorer byttes ut, slik at
        <code>/rsvp/&lt;svarlenke&gt;</code> blir <code>/rsvp/{'{id}'}</code>, og
        spørrestrengen fjernes i sin helhet. Svarlenker, e-postadresser og navn er derfor
        ikke med. Det settes heller ingen informasjonskapsler for måling – vi kjenner ikke
        igjen den samme nettleseren fra gang til gang.
      </p>
      <p>
        Grunnlaget er berettiget interesse i å drive tjenesten forsvarlig
        (personvernforordningen art. 6 nr. 1 bokstav f). Microsoft er databehandler, og
        målingene lagres i Norge.
      </p>

      <h2>Hvilke verktøy dere bruker</h2>
      <p>
        Vi teller hvilke av verktøyene – gjesteliste, budsjett, bordplassering og de andre –
        som blir åpnet. En rad er en teller: «14. september, dette bryllupet, bordplassering,
        brudepar, 3». Ikke hvem av dere, ikke når på dagen, og ikke hva dere gjorde inne i
        verktøyet.
      </p>
      <p>
        Vi lagrer verken bruker-ID eller noe som knytter tellingen til én av dere. Rollen står
        med – brudepar, toastmaster, forlover – fordi de bruker tjenesten til ulike ting, men
        den sier hvilken rolle, aldri hvem. Det settes ingen informasjonskapsel for dette.
      </p>
      <p>
        Hensikten er å bygge det dere faktisk trenger. Et verktøy ingen åpner, er enten
        unødvendig eller for godt gjemt, og uten tallene gjetter vi. Grunnlaget er berettiget
        interesse i å utvikle tjenesten (personvernforordningen art. 6 nr. 1 bokstav f). Vi har
        gjort og skrevet ned en avveining mot deres interesser, og du kan be om å få den.
      </p>
      <p>
        Vil dere ikke telles, skrur dere det av selv under{' '}
        <Link to="/app/personvern">Konto og personvern</Link>. Da sletter vi også det som
        allerede er talt. Tallene forsvinner uansett med bryllupet.
      </p>

      <h2>Annonsemåling</h2>
      <p>
        Kommer dere hit fra en søketreff, et innlegg eller en annonse, noterer vi{' '}
        <strong>hvilken kanal og hvilken kampanje</strong> besøket kom fra – «Google», «sosiale
        medier», navnet på annonsen. Det er en bøtte, ikke en identifikator: den sier hvor dere
        kom fra, aldri hvem dere er eller hva dere klikket på. Grunnlaget er berettiget interesse
        i å vite hvilke kanaler som gir kunder (art. 6 nr. 1 bokstav f), og det slettes{' '}
        {KAMPANJE_MAANEDER} måneder etter at kontoen ble opprettet.
      </p>
      <p>
        <strong>Klikk-ID-en er noe annet, og krever ditt ja.</strong> En annonselenke fra Google
        eller Meta bærer med seg en kode – <code>gclid</code> eller <code>fbclid</code> – som
        peker tilbake på nettopp det klikket. Den lagrer vi bare hvis du sier ja i boksen nederst
        på siden, og vi sletter den {KLIKKID_DAGER} dager etter at kontoen ble opprettet.
      </p>
      <p>
        Det du sier ja til, er todelt: at koden lagres på kontoen og i nettleseren din, og at vi{' '}
        <strong>kan melde et kjøp tilbake til Google eller Meta</strong> som en konvertering –
        altså at klikket deres førte fram. Da sendes koden og opplysningen om at et kjøp er gjort,
        ikke navn, e-postadresse eller noe om bryllupet. Google og Meta er selvstendig
        behandlingsansvarlige for det de gjør med det, etter sine egne vilkår, og behandlingen
        skjer også utenfor EØS.
      </p>
      <p>
        <strong>Dette har vi ikke gjort ennå.</strong> Vi har ikke kjøpt en eneste annonse, og
        ingen konvertering er meldt til noen. Samtykket står der for den dagen vi gjør det – og
        til da lagres ingen klikk-ID for den som ikke har sagt ja.
      </p>
      <p>
        Ombestemmer du deg, skrur du det av under{' '}
        <Link to="/app/personvern">Konto og personvern</Link>. Da sletter vi klikk-ID-en vi har.
        Det skal være like lett å trekke tilbake som å gi, og det er det.
      </p>

      <h2>Besøksstatistikk</h2>
      <p>
        Uten noe lagret i nettleseren kan vi telle besøk, men ikke se om ti besøk er ti personer
        eller én som kommer igjen. Sier du ja til besøksstatistikk i boksen nederst på siden,
        lager nettleseren din en <strong>tilfeldig ID</strong> og legger den i nettleserens eget
        lager.
      </p>
      <p>
        ID-en sendes med når du besøker forsiden, prislista eller registreringen. Vi lagrer
        tidspunktet, hvilken av sidene det var, og hvilken kanal du kom fra. Ikke IP-adressen,
        ikke hvilken nettleser du bruker, og ikke hvilken side du kom fra.
      </p>
      <p>
        Registrerer du deg, logger inn eller er innlogget når du besøker en av sidene,{' '}
        <strong>kobler vi ID-en til kontoen din</strong>, og noterer hvilken dato kontoen ble
        opprettet og hvor mange besøk som kom før. Da kan vi se hvilke kanaler og sider som
        fører til at noen registrerer seg og kjøper. En ID hører til én konto. Logger noen andre
        inn i samme nettleser, får nettleseren en ny ID. Besøk mens ingen er logget inn, regnes
        til kontoen ID-en er koblet til.
      </p>
      <p>
        I administrasjonen vises besøkene bare som tall for grupper av besøkende, aldri som en
        oversikt over hva én person har gjort. Tall som kobler besøk til kjøp, vises bare for
        grupper på minst fem besøkende.
      </p>
      <p>
        Formålet er å se hvor mange som besøker oss, hvor ofte de kommer tilbake, og hva som
        fører til registrering og kjøp. Grunnlaget er samtykket ditt (personvernforordningen
        art. 6 nr. 1 bokstav a). Ingenting av dette deles med andre.
      </p>
      <p>
        ID-en og besøkene som hører til den, slettes {BESOKSSTATISTIKK_MAANEDER} måneder etter
        det første besøket. Etter det lager nettleseren en ny ID. Sletter du kontoen, slettes
        besøkene som er koblet til den. Besøk som er koblet til kontoen din, er med i
        innsynskopien.
      </p>
      <p>
        Trekker du samtykket tilbake, sletter vi besøkene som hører til ID-en. Er du logget inn,
        sletter vi også besøk fra andre nettlesere som er koblet til kontoen din. Det gjør du med
        knappen under «Informasjonskapsler» her, eller under{' '}
        <Link to="/app/personvern">Konto og personvern</Link> når du er logget inn.
      </p>

      <h2>Svarlenkene til gjestene</h2>
      <p>
        Hver gjest får en lenke med en tilfeldig, ikke-gjettbar kode. Den som har lenken kan se
        navnene på brudeparet, dato og sted, og svare på vegne av den ene gjesten. Ingen andre
        gjester er synlige. Del derfor lenken bare med den den gjelder.
      </p>
      <p>
        <strong>Lenken slutter å virke {SVARLENKE_DAGER} dager etter bryllupsdagen.</strong> Da
        er det ingen vei inn lenger, heller ikke til programmet eller ønskelista – og det skjer
        lenge før opplysningene slettes. Skulle en lenke komme på avveie før den tid, kan
        brudeparet lage en ny til den gjesten; den gamle dør i det samme.
      </p>
      <p>
        <strong>Det gjesten har skrevet om mat, vises ikke tilbake på lenken.</strong> Hun får
        vite at noe er registrert, ikke hva, og kan skrive det på nytt eller be om å få det
        fjernet. Da kan ikke en videresendt lenke brukes til å lese allergien til den som eier
        den. Brudeparet ser den, for de skal bestille maten.
      </p>

      <h2>Informasjonskapsler</h2>
      <p>
        Informasjonskapslene vi setter, er nødvendige for at tjenesten skal virke. Vi teller
        besøk – hvor mange som var innom hvert steg, fra hvilken kanal, på hvilken slags enhet
        og om de var innlogget – uten egen kapsel og uten identifikator, slik at tellingen ikke
        kan gjøres om til en person. Det som går lenger enn det, spør vi om først.
      </p>
      <ul>
        <li>
          <strong>Innlogging:</strong> holder deg innlogget mellom sidevisninger. Settes når du
          logger inn, og slettes når du logger ut. Er du innlogget når du kommer til forsiden
          eller prislista, telles besøket som et kundebesøk i tellingen over. Ingenting mer
          lagres for det.
        </li>
        <li>
          <strong>Administrasjonsportalen:</strong> det samme for oss som drifter tjenesten. Den
          er skilt fra din egen, så en innlogging det ene stedet gir ingen tilgang det andre.
        </li>
        <li>
          <strong>Passordet mens siden bygges:</strong> husker at du har oppgitt det, så du
          slipper å skrive det på nytt for hver side.
        </li>
      </ul>
      <p>
        Alle tre er det <em>ekomloven § 3-15</em> kaller nødvendige for å levere tjenesten du
        selv har bedt om. Da skal du få vite om dem, men det kreves ikke samtykke.
      </p>
      <p>
        <strong>To ting er ikke nødvendige, og dem spør vi om:</strong> besøksstatistikken og
        klikk-ID-en fra en annonselenke. Boksen nederst på siden har derfor to knapper som begge
        betyr noe, og under «Velg selv» kan du svare på hver av dem for seg. Sier du nei, lagres
        verken besøks-ID eller klikk-ID, og du blir ikke spurt igjen før beskjeden endrer seg.
      </p>
      <p>
        I nettleserens eget lager, ikke i en informasjonskapsel, ligger derfor svaret ditt. Sa du
        ja til annonsemåling, ligger kampanjen du kom inn på der også, i inntil {KLIKKID_DAGER}{' '}
        dager. Sa du ja til besøksstatistikk, ligger den tilfeldige ID-en der. Den byttes ut etter{' '}
        {BESOKSSTATISTIKK_MAANEDER} måneder. Sier du nei etter å ha sagt ja, ligger ID-en der bare
        til vi har slettet besøkene.
      </p>
      <p>
        <button type="button" className="btn btn-secondary btn-sm" onClick={aapneValget}>
          Endre valget om informasjonskapsler
        </button>
      </p>

      <h2>Rettighetene dine</h2>
      <ul>
        <li>
          <strong>Innsyn og dataportabilitet:</strong> last ned alt vi har lagret som JSON-fil,
          under «Konto og personvern».
        </li>
        <li>
          <strong>Retting:</strong> alle opplysninger kan endres direkte i tjenesten.
        </li>
        <li>
          <strong>Sletting:</strong> sletter du kontoen, fjernes bryllupet, gjestene og alt
          innhold umiddelbart og permanent.
        </li>
        <li>
          <strong>Samtykke:</strong> e-post med tips er frivillig og kan skrus av når som helst.
          Vi sender aldri markedsføring uten at du har krysset av. Besøksstatistikk og
          annonsemåling endrer du under «Informasjonskapsler» over. Er du logget inn, gjelder
          valget om annonsemåling også kontoen din.
        </li>
        <li>
          <strong>Innsigelse:</strong> tre ting hviler på berettiget interesse – driftsmålingen,
          tellingen av hvilke verktøy dere bruker, og kanalen dere kom inn på. Dere kan protestere
          mot alle tre, uten å begrunne det. Tellingen skrur dere av selv under{' '}
          <Link to="/app/personvern">Konto og personvern</Link>; for de to andre, si fra på{' '}
          <a href={`mailto:${SELSKAP.epost}`}>{SELSKAP.epost}</a>. Opplysningene om gjestene
          hviler på det samme grunnlaget, men den innsigelsen er gjestens egen, og rettes til oss
          på den samme adressen.
        </li>
      </ul>

      <h2>Deling med andre</h2>
      <p>
        Vi selger ikke opplysninger, og bruker dem ikke til annet enn å levere tjenesten. For å
        kunne drive tjenesten bruker vi noen få leverandører som behandler opplysninger på våre
        vegne. Dette er hele lista:
      </p>
      <ul>
        <li>
          <strong>Microsoft Azure:</strong> drift, database, sikkerhetskopier og driftsmåling.
          Alt ligger i regionen Norway East, altså i Norge.
        </li>
        <li>
          <strong>Resend:</strong> utsending av e-post – invitasjoner og påminnelser til
          gjestene deres, kvitteringer og passordvarsler. De ser mottakerens navn og
          e-postadresse, og innholdet i meldingen.
          <br />
          <strong>Resend behandler i USA.</strong> Så lenge de er sertifisert under EU–US Data
          Privacy Framework, hviler overføringen på EU-kommisjonens adekvansbeslutning – den
          samme som lar opplysninger sendes til sertifiserte amerikanske selskaper uten noe
          mer. Databehandleravtalen vår med dem inneholder i tillegg EUs standardavtalevilkår
          (SCC): et kontraktsmessig vern, og en reserve dersom adekvansgrunnlaget en dag faller
          bort slik Privacy Shield gjorde. Hva vi har sjekket, og når, fører vi i vår egen
          vurdering av leverandøren.
        </li>
        <li>
          <strong>Fiken:</strong> regnskapet. Hver kvittering og hver kreditnota føres der, med
          salgsdokumentet vedlagt som PDF – og på det står navnet og e-postadressen til den som
          kjøpte. Bokføringsloven krever at vi tar vare på det. Ingenting fra gjestelista går
          til Fiken.
        </li>
      </ul>
      <p>
        Skal lista endres, sier vi fra i rimelig tid, slik at dere kan protestere.
      </p>
      <p>
        <strong>Google og Meta</strong> får noe fra oss bare hvis du har sagt ja til
        annonsemåling, og bare når vi annonserer: klikk-ID-en fra annonsen du kom inn på, og at
        et kjøp er gjennomført. Aldri navn, e-postadresse eller noe om bryllupet. Se
        «Annonsemåling» over – der står det også hvordan du trekker det tilbake.
      </p>
      <p>
        <strong>Betaling</strong> skjer gjennom Stripe eller Vipps, avhengig av hva dere velger.
        Begge behandler betalingsopplysningene som <em>selvstendig behandlingsansvarlig</em>,
        etter sine egne vilkår – ikke på våre vegne. Til Stripe sender vi e-postadressen deres,
        beløpet, hvilken pakke det gjelder og en intern referanse, slik at betalingen og deres
        egen kvittering kan håndteres. Til Vipps sender vi beløpet, en referanse og adressen
        dere skal tilbake til – ingen e-postadresse. Kortnummer og betalingsdetaljer passerer
        aldri våre servere; vi får vite at et kjøp er gjennomført, og beløpet.
      </p>

      <h2>Lagringstid</h2>
      <p>
        Det <strong>dere selv</strong> har lagt inn – bryllupet, budsjettet, oppgavene – lagres
        så lenge kontoen er aktiv. Det er deres, og dere kan slette det når som helst. Sletter
        du kontoen, fjernes alt med én gang.
      </p>
      <p>
        <strong>Opplysningene om gjestene</strong> sletter vi{' '}
        {LAGRINGSTID_MAANEDER} måneder etter bryllupsdatoen, uten at dere trenger å gjøre noe.
        Det gjelder navn, kontaktopplysninger, allergier, notater og bordplasseringen. Grunnen
        er enkel: gjestene har ikke inngått noe med oss. De fikk en lenke av dere, og da skal vi
        ikke sitte på allergiene deres i årevis etter at middagen ble servert.
      </p>
      <p>
        Vi sier fra på e-post {VARSEL_DAGER} dager før, slik at dere rekker å laste ned lista om
        dere vil ha den. Kontoen og deres eget innhold blir stående.
      </p>
      <p>
        Behandlingsprotokollen beholdes uten kobling til deg. Kvitteringer må vi oppbevare i fem
        år etter bokføringsloven § 13, og de er derfor unntatt fra begge fristene over.
      </p>

      <h2>Kontakt</h2>
      <p>
        Spørsmål om personvern, eller ønske om innsyn utover eksportfunksjonen, rettes til{' '}
        <a href={`mailto:${SELSKAP.epost}`}>{SELSKAP.epost}</a>.
      </p>
    </div>
  );
}
